import axios, { type AxiosInstance, type AxiosError } from 'axios'
import type { ApiResponse } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

class ApiClient {
  private client: AxiosInstance
  private accessToken: string | null = null
  private refreshToken: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Load tokens from storage
    this.loadTokens()

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiResponse<unknown>>) => {
        const originalRequest = error.config

        // Handle 401 and attempt refresh
        if (error.response?.status === 401 && originalRequest && !originalRequest.headers['X-Retry']) {
          try {
            await this.refreshAccessToken()
            originalRequest.headers['X-Retry'] = 'true'
            originalRequest.headers.Authorization = `Bearer ${this.accessToken}`
            return this.client(originalRequest)
          } catch {
            this.clearTokens()
            window.location.href = '/login'
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private loadTokens() {
    this.accessToken = localStorage.getItem('accessToken')
    this.refreshToken = localStorage.getItem('refreshToken')
  }

  private saveTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
  }

  clearTokens() {
    this.accessToken = null
    this.refreshToken = null
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  private async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken: this.refreshToken }
    )

    if (response.data.data) {
      this.saveTokens(response.data.data.accessToken, response.data.data.refreshToken)
    }
  }

  // Auth methods
  async login(email: string, password: string) {
    const response = await this.client.post<ApiResponse<{
      accessToken: string
      refreshToken: string
      mfaRequired?: boolean
      mfaToken?: string
    }>>('/auth/login', { email, password })

    if (response.data.data && !response.data.data.mfaRequired) {
      this.saveTokens(response.data.data.accessToken, response.data.data.refreshToken)
    }

    return response.data
  }

  async verifyMfa(mfaToken: string, code: string) {
    const response = await this.client.post<ApiResponse<{
      accessToken: string
      refreshToken: string
    }>>('/auth/mfa/verify', { mfaToken, code })

    if (response.data.data) {
      this.saveTokens(response.data.data.accessToken, response.data.data.refreshToken)
    }

    return response.data
  }

  async register(data: { email: string; password: string; name: string; tenantName: string }) {
    const response = await this.client.post<ApiResponse<{
      accessToken: string
      refreshToken: string
    }>>('/auth/register', data)

    if (response.data.data) {
      this.saveTokens(response.data.data.accessToken, response.data.data.refreshToken)
    }

    return response.data
  }

  async logout() {
    try {
      await this.client.post('/auth/logout')
    } finally {
      this.clearTokens()
    }
  }

  async getMe() {
    return this.client.get('/auth/me')
  }

  async setupMfa() {
    return this.client.post('/auth/mfa/setup')
  }

  async enableMfa(code: string) {
    return this.client.post('/auth/mfa/enable', { code })
  }

  async disableMfa(code: string) {
    return this.client.post('/auth/mfa/disable', { code })
  }

  // Generic CRUD methods
  async get<T>(url: string, params?: Record<string, unknown>) {
    const response = await this.client.get<ApiResponse<T>>(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: unknown) {
    const response = await this.client.post<ApiResponse<T>>(url, data)
    return response.data
  }

  async put<T>(url: string, data?: unknown) {
    const response = await this.client.put<ApiResponse<T>>(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: unknown) {
    const response = await this.client.patch<ApiResponse<T>>(url, data)
    return response.data
  }

  async delete<T>(url: string) {
    const response = await this.client.delete<ApiResponse<T>>(url)
    return response.data
  }
}

export const apiClient = new ApiClient()
